/**
 * Sosa-Stradonitz-toewijzing: welke voorouder staat op welk kwartiernummer?
 *
 * B-34 — dit stond drie keer los in de codebase (stamboom, waaier, dashboard) en
 * twee van die drie hadden dezelfde twee fouten. Eén implementatie voorkomt een
 * vierde.
 *
 * De twee fouten die hier zijn opgelost:
 *
 * 1. GESLACHT ALS ENIGE SLEUTEL. De dashboard-BFS deed
 *    `parent.gender === 'M' ? sosa*2 : sosa*2+1`. Twee ouders zonder ingevuld
 *    geslacht — of twee moeders — kregen daardoor allebei `sosa*2+1`, en de
 *    tweede overschreef de eerste. Dat is geen randgeval: 603 actieve personen
 *    in de dev-data hebben geen M/F, en het dashboard heeft er zelfs een eigen
 *    kwaliteitsmelding voor. Nu bepaalt het geslacht het VOORKEURSSLOT en wordt
 *    een bezet slot overgeslagen; zonder geslacht valt de ouder in het eerste
 *    vrije slot.
 *
 * 2. GLOBALE `visited`. Bij kwartierverlies (implex) bezet dezelfde voorouder
 *    meerdere Sosa-posities — dat hóórt ook zo. Met een globale set kreeg hij er
 *    één en verdween de hele tak daarboven, stil. Nu pad-lokaal: alleen de
 *    voorouders op het HUIDIGE pad blokkeren, wat nog steeds een echte cyclus
 *    afvangt (iemand die zijn eigen voorouder is, een datafout) maar implex
 *    gewoon toelaat. Zelfde aanpak als in de waaier (B-01) en de stamboom.
 *
 * Sosa-Stradonitz zelf is IMMUTABLE (zie rules/domain-knowledge.md): proband is
 * 1, vader van N is 2N, moeder van N is 2N+1.
 */

import type { Person, Relationship } from '../types.ts';
import { isBiologicalParentRel } from './pediType.ts';

/** Eén ouder-verwijzing; `gender` mag onbekend zijn. */
export interface SosaOuder {
  id: string;
  gender?: string;
}

export interface SosaToewijzingOpties {
  probandId: string;
  /** kind-id → zijn ouders. */
  childToParents: Map<string, SosaOuder[]>;
  /** Hoeveel generaties diep; generatie 0 is de proband. */
  maxGeneraties: number;
}

const isMannelijk = (g?: string) => g === 'M' || g === 'male';
const isVrouwelijk = (g?: string) => g === 'F' || g === 'female';

/**
 * Bouw de Sosa-kaart voor een kwartierstaat.
 *
 * @returns sosa-nummer → persoon-id. Eén persoon kan meerdere nummers hebben
 *   (kwartierverlies); dat is de bedoeling.
 */
export function buildSosaMap({
  probandId,
  childToParents,
  maxGeneraties,
}: SosaToewijzingOpties): Map<number, string> {
  const sosaMap = new Map<number, string>();
  if (!probandId || maxGeneraties < 1) return sosaMap;
  const maxSosa = Math.pow(2, maxGeneraties);

  const loop = (personId: string, sosa: number, opPad: Set<string>) => {
    if (sosa >= maxSosa) return;
    // Echte cyclus in de data: iemand is zijn eigen voorouder.
    if (opPad.has(personId)) return;
    opPad.add(personId);
    sosaMap.set(sosa, personId);

    const ouders = childToParents.get(personId) ?? [];
    // Eerst de ouders MET geslacht: die hebben een voorkeursslot en mogen dat
    // opeisen voordat een ouder zonder geslacht het eerste vrije slot pakt.
    const gesorteerd = [...ouders].sort((a, b) => {
      const aBekend = isMannelijk(a.gender) || isVrouwelijk(a.gender) ? 0 : 1;
      const bBekend = isMannelijk(b.gender) || isVrouwelijk(b.gender) ? 0 : 1;
      return aBekend - bBekend;
    });

    const vaderSlot = sosa * 2;
    const moederSlot = sosa * 2 + 1;
    for (const ouder of gesorteerd) {
      // Voorkeursslot op geslacht; is dat bezet, dan het andere slot. Twee
      // moeders (of twee vaders) horen allebei zichtbaar te zijn — de tweede in
      // het overgebleven slot tonen is eerlijker dan haar laten vallen. Is
      // ALLES bezet (drie ouderrelaties), dan overslaan in plaats van een
      // bestaande voorouder overschrijven.
      const voorkeur = isVrouwelijk(ouder.gender) ? moederSlot : vaderSlot;
      const anders = voorkeur === vaderSlot ? moederSlot : vaderSlot;
      let slot: number | null = null;
      if (!sosaMap.has(voorkeur)) slot = voorkeur;
      else if (!sosaMap.has(anders)) slot = anders;
      if (slot === null) continue;
      loop(ouder.id, slot, opPad);
    }

    opPad.delete(personId);
  };

  loop(probandId, 1, new Set());
  return sosaMap;
}

/** Generatie van een Sosa-nummer. Proband (1) = generatie 0. */
export function generatieVanSosa(sosa: number): number {
  return Math.floor(Math.log2(sosa));
}

/**
 * Het generatienummer zoals de genealoog het ziet: proband = 1, ouders = 2,
 * Sosa 229 = 8. Gelijk aan de generatiebalk (NC #689) en het rapport; gedeeld door
 * het detailpaneel en het exportveld "Relatie tot proband" (NC #844, #1792).
 */
export const getoondeGeneratie = (sosa: number): number => generatieVanSosa(sosa) + 1;

/**
 * De proband plus al zijn directe voorouders — de personen die op de
 * Statistieken-pagina overblijven met "Alleen directe voorouders" aan (#1511,
 * NC #747).
 *
 * Bloedlijn, niet "iedereen die in de boom boven je hangt": Sosa-Stradonitz IS
 * afstamming, dus adoptie-/pleeg-/stiefouders vallen erbuiten
 * (`isBiologicalParentRel`). Zonder die guard zou een 'spousal'-rel bovendien
 * als ouder tellen — personB is daar de partner, niet het kind.
 *
 * `sosa_number` uit de DB is hier bewust NIET gebruikt: die kolom is in de
 * praktijk leeg (B-33), dus hij wordt net als op het dashboard herberekend.
 */
export function directeVoorouderIds(
  probandId: string | undefined,
  people: readonly Person[],
  relationships: readonly Relationship[],
  maxGeneraties = 15
): Set<string> {
  if (!probandId) return new Set();
  const genderVan = new Map(people.map((p) => [p.id, p.gender]));
  const childToParents = new Map<string, SosaOuder[]>();
  for (const rel of relationships) {
    if (!isBiologicalParentRel(rel)) continue;
    const lijst = childToParents.get(rel.personB) ?? [];
    lijst.push({ id: rel.personA, gender: genderVan.get(rel.personA) });
    childToParents.set(rel.personB, lijst);
  }
  return new Set(buildSosaMap({ probandId, childToParents, maxGeneraties }).values());
}

/**
 * Onderzoekstypes zoals opgeslagen in `project.settings.researchType`.
 * Eén lijst voor het aanmaak- én het bewerkvenster (NC #659.1: het
 * bewerkvenster kende er maar vijf en zette een reeks stil terug op kwartierstaat).
 */
export const RESEARCH_TYPES = [
  'kwartierstaat',
  'stamboom',
  'genealogisch',
  'regionaal',
  'stamreeks',
  'naamreeks',
  'afstammingreeks',
  'parenteel',
  'overig',
] as const;
export type ResearchType = (typeof RESEARCH_TYPES)[number];

/** Eén persoon per generatie: stamreeks, naamreeks, afstammingsreeks. */
export function isLineairOnderzoek(researchType: string | null | undefined): boolean {
  return (
    researchType === 'stamreeks' ||
    researchType === 'naamreeks' ||
    researchType === 'afstammingreeks'
  );
}

/**
 * NC #659.1 — verwacht aantal voorouders in generatie `gen` (proband = 0).
 * Een kwartierstaat verdubbelt per generatie (2^gen); een reeks is één lijn en
 * verwacht er precies één. Anders scoort een complete stamreeks nooit boven 50%.
 */
export function verwachtPerGeneratie(researchType: string | null | undefined, gen: number): number {
  return isLineairOnderzoek(researchType) ? 1 : Math.pow(2, gen);
}
